import FirmLike from "./like";
import { MAX_LEN, RESPOND_LINKS, LOADING_CLASS } from "./const";
import FirmReviewReport from "./report";
import { store } from "~/state/store";
import { node, icon } from "~/util/dom";
import { deferPromise } from "~/util/general";
import { createVerifiedUserTooltip } from "~/util/userweb";
import { isFirmFromReview, clamp, getFirmImpressConfig, hasFilledStructuredReviews } from "~/util/review";
import { createReviewPhotos } from "~/ui/review/photos";
import { putReviewReply } from "~/util/providers/review";
import { IReviewItem, IReviewItemLike } from "./interfaces";
import { IDeferPromise } from "~/interfaces";
import { CONFIG } from "~/config";
import { _ } from "~/i18n";
import { createDeepLTranslate, IReviewDeepLData } from "~/ui/deepl/review";
import { createEmojisPreview } from "~/ui/review/emojisPreview";
import { unsetDetail } from "~/state/detail/state";
import { createSmallStars } from "~/ui/review/stars";
import Notifications, { NOTIFICATIONS_TYPE } from "~/util/notifications";
import { detail } from "~/util/providers/poiagg";

interface IFirmReviewItem {
	data: IReviewItem;
	isDetailInactive?: boolean;
	isOwner?: boolean;
	isFirm?: boolean;
	isTwin?: boolean;
	logSource?: string;
	commentsSync?: boolean;
	index?: number;
	onOpen?: () => void;
	backAction?: null | (() => void);
	analytics?: object;
	poi?: {
		source: string;
		id: string | number;
		title: string;
		poitypeId: number;
		mark: {
			lon: number;
			lat: number;
		}
	};
}

// zobrazeni jedne polozky s hodnocenim - zobrazime moznou reakci, zbozi, positiva, negativa
export default class FirmReviewItem {
	_opts: IFirmReviewItem;

	_dom: {
		[key: string]: null | HTMLElement;
		text?: HTMLTextAreaElement;
	};

	_data: IReviewItem;

	_isZbozi: boolean;

	_plusMinusType: boolean;

	_isOwner: boolean;

	_isRespondOpen: boolean;

	_isOptsOpened: boolean;

	_firmLike: FirmLike;

	_firmImpressWasSend: boolean;

	_clampIsShortened: boolean;

	_firstClampPromiseData: IDeferPromise<any>;

	constructor(optsArg: IFirmReviewItem) {
		const opts = {
			data: {},
			isDetailInactive: false,
			isOwner: false, // rusAdmin
			isFirm: false,
			isTwin: false,
			logSource: "recenze-poi",
			commentsSync: false, // synchronizovat like s komentari v detailu
			index: 0, // index itemu vuci rodici
			// signal pri otevreni opts pro ostatni
			onOpen: () => { /**/ },
			backAction: null,
			analytics: {},
			...optsArg,
		};

		this._dom = {
			container: node("div", { className: "firm-review-item" }),
			respond: null,
		};

		this._opts = opts;
		this._data = opts.data;
		this._isZbozi = this._data.review_source === "zbozi";
		this._plusMinusType = (this._data.positives && this._data.positives.length > 0) || (this._data.negatives && this._data.negatives.length > 0);
		this._isOwner = opts.isOwner;
		this._isRespondOpen = false;
		this._isOptsOpened = false;
		this._firmLike = null;
		this._firmImpressWasSend = false;
		this._clampIsShortened = false;
		this._firstClampPromiseData = deferPromise();

		this._build();
		this._buildStructuredRating();
		this._buildPhotos();
		this._buildLike();
		this._buildReply();
	}

	destroy() {
		this._dom.container.remove();
	}

	get container() {
		return this._dom.container;
	}

	get header() {
		return this._dom.header;
	}

	get data() {
		return this._data;
	}

	closeOpts() {
		if (this._isOptsOpened) {
			this._dom.opts.classList.remove("open");
			document.body.removeEventListener("click", this);
			this._isOptsOpened = false;
		}
	}

	updateLike(data: IReviewItemLike) {
		if (this._firmLike && data.id === this._firmLike.id) {
			this._firmLike.data = data;
		}
	}

	handleEvent(event: MouseEvent) {
		// document body click
		if (event.type === "click") {
			this.closeOpts();
		}
	}

	clamp() {
		// pro zbozi skip
		if (this._isZbozi) {
			this._firstClampPromiseData.resolve({});

			return;
		}

		setTimeout(() => {
			// recenze
			if (this._dom.desc) {
				this._clampIsShortened = clamp(this._dom.desc, true, () => {
					this._firmImpressAnalytics(true);
				});
			}

			this._firstClampPromiseData.resolve({});

			if (this._dom.replyDesc) {
				clamp(this._dom.replyDesc, true);
			}
		}, 0);
	}

	firmImpressAnalytics() {
		if (this._firmImpressWasSend) {
			return true;
		}

		this._firmImpressAnalytics();

		this._firmImpressWasSend = true;

		return false;
	}

	_firmImpressAnalytics(reviewShowMoreAction = false) {
		this._firstClampPromiseData.promise.then(() => {
			const config = getFirmImpressConfig({
				reviewId: this._data.id,
				state: this._data.state,
				reviewOrder: this._opts.index,
				reviewShortened: reviewShowMoreAction || this._clampIsShortened,
				reviewAuthor: !!this._isOwner,
				reviewShowMoreAction,
			});

			Mapy.getComponent("logger").log(config.action, config.data);
		}, () => { /**/ });
	}

	_build() {
		const { user } = store.getState().user;
		const userPublicId = this._isZbozi || this._data.user.review_count <= 0 ? null : this._data.user.public_id;

		// rozdeleni
		const header = userPublicId && !CONFIG.isScreenshoter ? node("a", { className: "header-part", href: `https://${CONFIG.BASE_URL}/?up=${userPublicId}&upc=reviews`, target: "_blank" }) : node("div", { className: "header-part" });
		const imgHolder = node("mapy-avatar", { className: "img-holder", size: 40, premium: this._data.user.hasPremium ? "mapy" : "none" });

		if (this._data.user.image_url) {
			const userBadge = node("img", { src: this._data.user.image_url });

			userBadge.crossOrigin = "anonymous";
			imgHolder.appendChild(userBadge);
		} else {
			imgHolder.classList.add("svg-avatar");
			imgHolder.appendChild(icon("avatar-anonym"));
		}

		header.appendChild(imgHolder);
		const infoPart = node("span", { className: "info-part" });
		const title = node("h2", { className: this._data.user.name ? "" : "anonym" });

		title.append(
			node("div", { textContent: (this._data.user.name || _("detail.content.review2.anonymous")).trim() }),
		);

		if (this._data.user.bank_id) {
			const verifiedIcon = icon("verifieduser16");

			title.appendChild(verifiedIcon);
			createVerifiedUserTooltip(verifiedIcon);
		}

		infoPart.appendChild(title);
		header.appendChild(infoPart);
		this._dom.container.appendChild(header);
		this._dom.header = header;

		// hodnoceni a datum
		const reviewCont = node("div", { className: "review-cont" });

		// stars count
		reviewCont.appendChild(createSmallStars({
			rating: this._data.rating,
		}));
		// datum
		const date = node("span", { className: `date`, textContent: Mapy.Util.dateFormat(new Date(this._data.updated), "d. mn yyyy") });

		reviewCont.appendChild(date);

		this._dom.container.appendChild(reviewCont);

		if (userPublicId) {
			const allReviews = node("p", { className: `all-reviews`, textContent: _("detail.content.booking.reviewCount2", { count: this._data.user.review_count || 0 }) });

			allReviews.appendChild(icon("forward"));
			infoPart.appendChild(allReviews);

			const backAction = this._opts.backAction;

			!CONFIG.isScreenshoter && header.addEventListener("click", function headerClick(event) {
				event.preventDefault();
				Mapy.getComponent("userProfile").setState({
					up: userPublicId,
					upc: "reviews",
					...backAction ? { backAction } : {},
				}).then(() => {
					store.dispatch(unsetDetail());
					Mapy.getComponent("history").save();
				}, () => { /**/ });
			});
		} else {
			infoPart.classList.add("only-title");
		}

		// zbozi badge
		if (this._isZbozi) {
			const zboziInfo = node("span", { className: "zbozi-info" });

			zboziInfo.appendChild(icon("badge-check"));
			zboziInfo.appendChild(node("span", { className: "bubble", textContent: _("detail.content.review2.zboziCert") }));
			reviewCont.appendChild(zboziInfo);
		}

		// analyticky mod pro firemni hodnoceni
		if (Mapy.CUS.userweb && Mapy.CUS.userweb.firmDebug) {
			const debugTitle = node("p", { innerHTML: `Celková váha: <strong>${(this._data.weight || 0).toFixed(3)}</strong>`, style: "font-style: italic; color: gray;" });

			this._dom.container.appendChild(debugTitle);
		}

		let textToTranslation: IReviewDeepLData = null;

		// plus minus popisek
		if (this._plusMinusType) {
			const positives = this._data.positives || [];
			const transformCb = arrayStr => {
				let outputStr = arrayStr.join(", ");

				// odstraneni duplicitnich carek
				outputStr = outputStr.replace(/,\s*,/ug, ",");

				return outputStr;
			};

			if (positives.length) {
				const positivesEl = node("p", { className: "positives" });
				const text = node("span", { className: "desc", textContent: transformCb(positives) });

				positivesEl.appendChild(node("span", { className: "plus-icon" }));
				positivesEl.appendChild(text);
				this._dom.container.appendChild(positivesEl);

				if (this._data.lang && this._data.user?.public_id !== user.userPublicId) {
					const deeplPositives = createDeepLTranslate({
						text: transformCb(positives),
						lang: this._data.lang,
						container: text,
						reviewId: this._data.id,
						type: "positives",
						native: this._data.native || !("native" in this._data),
					}, "review");

					deeplPositives && this._dom.container.appendChild(deeplPositives);
				}
			}

			const negatives = this._data.negatives || [];

			if (negatives.length) {
				const negativesEl = node("p", { className: "negatives" });
				const text = node("span", { className: "desc", textContent: transformCb(negatives) });

				negativesEl.appendChild(node("span", { className: "minus-icon" }));
				negativesEl.appendChild(text);
				this._dom.container.appendChild(negativesEl);

				if (this._data.lang && this._data.user?.public_id !== user.userPublicId) {
					const deeplNegatives = createDeepLTranslate({
						text: transformCb(negatives),
						lang: this._data.lang,
						container: text,
						reviewId: this._data.id,
						type: "negatives",
						native: this._data.native || !("native" in this._data),
					}, "review");

					deeplNegatives && this._dom.container.appendChild(deeplNegatives);
				}
			}
		// textovy popisek
		} else {
			const desc = node("p");

			if (!this._data.text && this._data.reply && this._data.reply.text) {
				desc.textContent = _("detail.content.review2.emptyText");
				desc.classList.add("empty");
			} else {
				desc.innerText = this._data.text;

				if (this._data.text && this._data.lang && this._data.user?.public_id !== user.userPublicId) {
					textToTranslation = {
						text: this._data.text,
						lang: this._data.lang,
						container: desc,
						reviewId: this._data.id,
						type: "text",
						native: this._data.native || !("native" in this._data),
					};
				}
			}

			this._dom.desc = desc;
			this._dom.container.appendChild(desc);
		}

		if (textToTranslation) {
			const deepl = createDeepLTranslate(textToTranslation, "review");

			deepl && this._dom.container.appendChild(deepl);
		}

		// source zbozi - nechceme hodnotit; taky to nechceme pro vlastnika
		if (!this._isZbozi && !this._isOwner) {
			const opts = node("span", { className: "opts" });

			opts.appendChild(icon("etc-vertical"));
			const reportBtn = node("button", { className: "report-btn", textContent: _("photoContextMenu.reportBad") });

			reportBtn.setAttribute("data-log-ignore", "");
			reportBtn.addEventListener("click", event => {
				event.stopPropagation();
				event.preventDefault();
				// analytika pro mapy
				this._log("click", {
					text: "Nahlasit problem",
					section: "reviews",
					...this._opts.analytics,
				});
				this.closeOpts();

				// musime byt prihlaseni
				const { state } = store.getState().user.user;

				if (state === "green") {
					this._openReportDialog();
				} else {
					// musime se prihlasit
					Mapy.getComponent("login").open({
						analyticsData: {
							loginOrigin: "reviewsReport",
							section: "reviews",
						},
						motivation: "report",
					}).then(() => {
						const { data: detailData } = store.getState().detail;

						if (detailData) {
							// po loginu znovu detail
							detail(detailData.source, detailData.id).promise.then(data => {
								// aktualizujeme data
								this._isOwner = data.poi.rus_admin;

								let isError = false;

								if (this._isOwner) {
									// analytika
									this._log("errorMessage", {
										type: "ownerReview",
									});
									Notifications.notify({
										content: _("detail.content.review2.report.error429Owner"),
										type: NOTIFICATIONS_TYPE.ERROR,
									});
									isError = true;
								}

								// zkusime test na vlastni recenzi
								if (data.poi.myReview && data.poi.myReview.id === this._data.id) {
									// analytika
									this._log("errorMessage", {
										type: "ownerProblemReport",
									});
									Notifications.notify({
										content: _("detail.content.review2.report.error429Review"),
										type: NOTIFICATIONS_TYPE.ERROR,
									});
									isError = true;
								}

								if (!isError) {
									// otevreme dialog
									this._openReportDialog();
								}
							}, () => {
								this._openReportDialog();
							});
						} else {
							this._openReportDialog();
						}
					}, () => { /**/ });
				}
			});
			opts.addEventListener("click", event => {
				event.stopPropagation();
				event.preventDefault();

				if (this._isOptsOpened) {
					this.closeOpts();
				} else {
					// signal o otevreni opts
					this._opts.onOpen();
					document.body.addEventListener("click", this);
					opts.classList.add("open");
					this._isOptsOpened = true;
				}
			});
			opts.appendChild(reportBtn);
			this._dom.container.appendChild(opts);
			this._dom.opts = opts;
		}
	}

	_buildPhotos() {
		if (this._hasPhotos()) {
			this._dom.container.appendChild(createReviewPhotos({
				reviewData: {
					...this._data,
					...this._opts.poi ? { poi: this._opts.poi } : {},
				},
			}));
		}
	}

	_buildStructuredRating() {
		this._dom.container.appendChild(createEmojisPreview({
			config: this._data.structured_rating || [],
		}).container);
	}

	_buildLike() {
		// pouze pro vyplneny text nebo zbozi (plus/minus type)
		const hasPhotos = this._hasPhotos();

		if (this._data.text || this._plusMinusType || hasPhotos) {
			const likeCont = node("div", { className: "like-cont" });
			const { data } = store.getState().detail;
			const firmLike = new FirmLike({
				id: this._data.likes ? this._data.likes.id : `review.firm.${this._data.id}`,
				data: this._data.likes,
				commentsSync: this._opts.commentsSync,
				index: this._opts.index,
				disabled: this._opts.isDetailInactive,
				detail: {
					source: data?.source,
					id: data?.id as number,
					typeId: data?.typeId,
				},
				analytics: {
					photos: hasPhotos ? 1 : 0,
					attributes: hasFilledStructuredReviews(this._data.structured_rating),
				},
			});

			// vlastni hodnoceni
			likeCont.appendChild(firmLike.container);
			this._dom.container.appendChild(likeCont);
			this._firmLike = firmLike;
		}
	}

	_buildReply() {
		if (this._data.reply && this._data.reply.text) {
			const replyCont = node("div", { className: "reply-cont" });
			const replyInner = node("div", { className: "reply-inner" });
			const circle = node("span", { className: "circle" });

			circle.appendChild(icon("reply"));
			replyInner.appendChild(circle);
			const caption = node("h2", { className: "reply-title", textContent: _("detail.content.review2.reaction") });
			const replyDesc = node("p", { textContent: `${this._data.reply.text}` });

			// datum
			const date = node("span", { className: `date`, textContent: Mapy.Util.dateFormat(new Date(this._data.reply.updated), "d. mn yyyy") });

			replyInner.appendChild(caption);
			replyInner.appendChild(replyDesc);

			if (!this._isOwner && this._data.reply.lang) {
				const deepl = createDeepLTranslate({
					text: replyDesc.textContent,
					lang: this._data.reply.lang,
					container: replyDesc,
					reviewId: this._data.id,
					type: "reply",
					native: this._data.reply.native || !("native" in this._data.reply),
				}, "review");

				deepl && replyInner.appendChild(deepl);
			}

			replyCont.appendChild(replyInner);
			this._dom.container.appendChild(replyCont);

			Object.assign(this._dom, {
				replyDesc, date,
			});

			// reagovat - editace
			if (this._isOwner && !this._isZbozi) {
				const respondBtn = node("button", { className: "edit-btn", textContent: _("edit") });

				respondBtn.setAttribute("data-log-ignore", "");
				respondBtn.addEventListener("click", () => {
					// analytika pro mapy
					this._log("click", {
						text: "Upravit reakci",
					});
					this._openRespond();
				});
				replyCont.classList.add("myreply");
				replyInner.appendChild(respondBtn);
			}
			caption.appendChild(date);
		} else if (this._isOwner && !this._isZbozi && isFirmFromReview(this._data)) {
			// reagovat - typ recenze je firemni
			const respondBtns = node("div", { className: "respond-btns" });
			const respondBtn = node("button", { className: "respond-btn", textContent: _("detail.content.review2.respond") });

			respondBtn.setAttribute("data-log-ignore", "");
			respondBtn.addEventListener("click", () => {
				// analytika pro mapy
				this._log("click", {
					text: "Reagovat",
				});
				this._openRespond();
			});

			respondBtns.appendChild(respondBtn);
			this._dom.container.appendChild(respondBtns);
			this._dom.respondBtns = respondBtns;
		}
	}

	_openRespond() {
		// odpoved
		if (this._isRespondOpen) {
			return;
		}

		this._dom.container.classList.add("respond-open");

		const respondCont = node("div", { className: "respond-cont" });
		const caption = node("h2", { className: "title", textContent: `${_("detail.content.review2.ownRespond")}:` });
		const text = node("textarea", { className: "text", placeholder: _("detail.content.review.description") });

		text.setAttribute("maxlength", MAX_LEN.toString());
		text.value = this._data.reply ? this._data.reply.text || "" : "";

		text.addEventListener("input", () => {
			this._updateRest();
		});

		const counter = node("p", { className: "counter" });
		// zobrazeni licence
		const lang = CONFIG.defaultLang;
		const agreement = node("p", {
			className: "licence",
			innerHTML: `${_("detail.content.review2.acceptLicense", {
				link: ["cs", "sk"].indexOf(lang) === -1 ? RESPOND_LINKS.en : RESPOND_LINKS.cs,
			})}.`,
		});
		const agreementLink = agreement.querySelector("a");

		agreementLink.setAttribute("data-log-ignore", "");
		agreementLink.addEventListener("pointerdown", () => {
			// analytika pro mapy
			this._log("click", {
				text: "Smluvními podmínkami",
			});
		});

		const saveBtn = node("button", { className: "save-btn", textContent: _("detail.content.review2.saveRespond") });

		saveBtn.setAttribute("data-log-ignore", "");
		saveBtn.addEventListener("click", () => {
			// analytika pro mapy
			this._log("click", {
				text: "Uložit reakci",
			});
			this._save();
		});

		const cancelBtn = node("button", { className: "cancel-btn", textContent: _("cancel") });

		cancelBtn.setAttribute("data-log-ignore", "");
		cancelBtn.addEventListener("click", () => {
			// analytika pro mapy
			this._log("click", {
				text: "Zrušit reakci",
			});
			this._closeRespond();
		});

		respondCont.appendChild(caption);
		respondCont.appendChild(text);
		respondCont.appendChild(counter);
		respondCont.appendChild(agreement);
		respondCont.appendChild(saveBtn);
		respondCont.appendChild(cancelBtn);

		Object.assign(this._dom, {
			respondCont, counter, text, saveBtn,
		});
		this._dom.container.appendChild(respondCont);

		this._updateRest();
		text.focus();

		this._isRespondOpen = true;
	}

	_closeRespond() {
		if (!this._isRespondOpen) {
			return;
		}

		this._dom.container.classList.remove("respond-open");
		this._dom.respondCont.remove();
		this._dom.respondCont = null;
		this._isRespondOpen = false;
	}

	_save() {
		let wasEmpty = false;

		if (!this._data.reply) {
			this._data.reply = {
				/* eslint-disable camelcase */
				bank_id: false,
				image_url: "",
				/* eslint-enable camelcase */
				name: "",
				text: "",
				updated: "",
			};
			wasEmpty = true;
		}

		if (typeof this._data.reply.text === "undefined") {
			this._data.reply.text = "";
			wasEmpty = true;
		}

		const newValue = this._dom.text.value.trim();

		// stejna hodnota - zavreme
		if (newValue === this._data.reply.text) {
			// zavreme
			this._closeRespond();

			return;
		}

		this._dom.respondCont.classList.add(LOADING_CLASS);

		putReviewReply({
			reviewId: this._data.id,
			text: newValue,
		}).then(() => {
			// analytika pro mapy
			this._log("reakce-leti", {
				type: wasEmpty ? "reakce" : "reakce-edit",
			});

			this._dom.respondCont.classList.remove(LOADING_CLASS);
			this._data.reply.text = newValue;
			this._data.updated = new Date().toString();

			if (wasEmpty) {
				this._buildReply();
				// odstranime tlacitko
				this._dom.respondBtns.remove();
			}

			if (!this._data.text && this._dom.desc) {
				this._dom.desc.textContent = _("detail.content.review2.emptyText");
				this._dom.desc.classList.add("empty");
			}

			this._dom.replyDesc.innerText = this._data.reply.text;
			this._dom.date.textContent = Mapy.Util.dateFormat(new Date(), "d. mn yyyy");
			// zavreme
			this._closeRespond();
			// upravime orez
			this.clamp();
		}, error => {
			// eslint-disable-next-line no-console
			console.error(error);
			this._dom.respondCont.classList.remove(LOADING_CLASS);
			Notifications.notify({
				content: _("detail.content.review2.errorSave"),
				type: NOTIFICATIONS_TYPE.ERROR,
			});
		});
	}

	_updateRest() {
		const chars = MAX_LEN - this._dom.text.value.length;

		this._dom.counter.innerHTML = _("detail.content.review.numOfChar", {
			chars,
		});

		if (chars === MAX_LEN) {
			this._dom.saveBtn.setAttribute("disabled", "disabled");
		} else {
			this._dom.saveBtn.removeAttribute("disabled");
		}
	}

	_openReportDialog() {
		/* eslint-disable no-new */
		new FirmReviewReport({
			id: this._data.id,
			isFirm: this._opts.isFirm,
			isTwin: this._opts.isTwin,
			isOwner: this._isOwner,
			logSource: this._opts.logSource,
			analytics: this._opts.analytics,
			hasPhotos: this._hasPhotos(),
		});
	}

	_hasPhotos() {
		return Array.isArray(this._data.gallery) && this._data.gallery.length > 0;
	}

	_log(name: string, data: object) {
		Mapy.getComponent("logger").log(name, {
			source: this._opts.logSource,
			...data,
		});
	}
}
